import { useLayoutEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { IDocumentBlock } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import DocumentBox from '@molecules/DocumentBox';
import Row from '../Row/Row';
import './Block.scss';
import { IUniqueKey } from '@molecules/formbuilder/interfaces/IUniqueKey';
import { IDocumentScheme } from '@models/document/IDocumentScheme';
import { useActivated, useFormulaDisplay, useFormulaState } from '@molecules/formbuilder/hooks';
import { useDebugPanel } from '@/context/DebugPanelContext';
import FieldFormulaTooltip from '@molecules/DebugPanel/FieldFormulaTooltip/FieldFormulaTooltip';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';
import { DocumentFormulaTraceParentContext } from '@/context/DocumentFormulaTraceContext';
import { DocumentTableCountContext, useTableCountScope } from '@/context/DocumentTableCountContext';
import BlockTableCountHeader from './BlockTableCountHeader';

export interface IBlockProps<TFieldValues extends object = object> extends IUniqueKey {
    block?: IDocumentBlock;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    docId?: string;
    docScheme?: IDocumentScheme;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

const DocumentBlock = <TFieldValues extends object = object>({
    block,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    docId,
    uniqueKey,
    docScheme,
    ensureExternalDataSourceLoaded,
}: IBlockProps<TFieldValues>) => {
    const { activated } = useActivated();
    const countScope = useTableCountScope(block?.id);
    const { settings: debugSettings } = useDebugPanel();
    const visibility = useFormulaState(block?.visibilityRules, fields, formMethods, isEdit, isNew, activated, {
        fallback: true,
        ensureExternalDataSourceLoaded,
        trace: block
            ? {
                  targetId: block.id,
                  targetKind: 'block',
                  targetName: block.name,
                  ruleKind: 'visibility',
              }
            : undefined,
    });

    // Заголовок блока может считаться формулой (`nameFormula`). Пустой результат — не имя:
    // откатываемся на статический `name`, иначе блок с незаполненными зависимостями формулы
    // остался бы без заголовка (а по условию рендера ниже — и вовсе не отрисовался бы).
    const nameFromFormula = useFormulaDisplay(block?.nameFormula, fields, formMethods, isEdit, isNew, activated, {
        evalDisplayFormat: block?.evalNameFormula,
        evaluateOnEmpty: true,
        ensureExternalDataSourceLoaded,
        trace: block
            ? {
                  targetId: block.id,
                  targetKind: 'block',
                  targetName: block.name,
                  ruleKind: 'formula',
              }
            : undefined,
    });
    const name = nameFromFormula.value || block?.name;
    const countStore = countScope.store;
    useLayoutEffect(() => {
        countStore.setTitle(name);
    }, [countStore, name]);

    const headerContent = debugSettings.showFieldFormulas ? (
        <>
            {name}
            <FieldFormulaTooltip
                visibilityRules={block?.visibilityRules}
                nameFormula={block?.nameFormula}
                fieldKey={block?.id}
            />
        </>
    ) : (
        name
    );

    return block && block.id && name && visibility ? (
        <DocumentFormulaTraceParentContext value={{ targetId: block.id, targetKind: 'block' }}>
            <DocumentTableCountContext value={countScope}>
                <DocumentBox
                    header={<BlockTableCountHeader store={countScope.store}>{headerContent}</BlockTableCountHeader>}
                    id={block.id}
                    className={`document-block${!isEdit && !isNew ? ' document-block--view' : ''}`}
                    formulaTraceTargetId={block.id}
                >
                    {block.rows.row.map((item, i) => (
                        <Row
                            row={item}
                            formMethods={formMethods}
                            docId={docId}
                            setError={setError}
                            fields={fields}
                            isEdit={isEdit}
                            isNew={isNew}
                            turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                            key={i}
                            includedFields={includedFields}
                            uniqueKey={uniqueKey}
                            docScheme={docScheme}
                            ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
                        />
                    ))}
                </DocumentBox>
            </DocumentTableCountContext>
        </DocumentFormulaTraceParentContext>
    ) : (
        <></>
    );
};

export default DocumentBlock;
